// src/components/GroupManagement.jsx
import { useState, useEffect, useContext } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { AuthContext } from "../context/AuthContext";
import { db } from "../firebase/firebase";

export default function GroupManagement() {
  const [groups, setGroups] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const { userData } = useContext(AuthContext);

  useEffect(() => {
    if (!userData) return;                 // wait for auth + user doc
    if (userData.role !== "admin") return; // groups are admin-managed

    loadGroups();
  }, [userData]);

  const createGroup = async () => {
    if (!groupName.trim()) return alert("Please enter a group name");

    await addDoc(collection(db, "groups"), {
      name: groupName.trim(),
      description: groupDescription.trim(),
      createdAt: serverTimestamp(),
    });

    setGroupName("");
    setGroupDescription("");
    setShowCreate(false);
    await loadGroups();
  };

  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    await deleteDoc(doc(db, "groups", id));
    await loadGroups();
  };

  return (
    <div className="admin-card form-container">
      <div className="admin-card-header">
        <div>
          <h2 className="admin-card-title">🏷️ Group Management</h2>
          <p className="admin-card-subtitle">
            Groups control which folders users can see.
          </p>
        </div>

        <button
          className="btn btn-primary"
          type="button"
          onClick={() => setShowCreate((s) => !s)}
        >
          {showCreate ? "Close" : "Create Group"}
        </button>
      </div>

      {showCreate && (
        <div className="admin-surface">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Group Name</label>
              <input
                className="form-input"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., SIT, CPC, Student Support"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <input
                className="form-input"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary" type="button" onClick={createGroup}>
              Save Group
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <h3 className="admin-subtitle">Existing Groups</h3>

        {groups.length === 0 ? (
          <div className="admin-muted">No groups yet.</div>
        ) : (
          <div className="admin-grid">
            {groups.map((g) => (
              <div key={g.id} className="admin-tile">
                <div>
                  <div className="admin-tile-title">{g.name}</div>
                  {g.description ? (
                    <div className="admin-muted" style={{ marginTop: 6 }}>
                      {g.description}
                    </div>
                  ) : null}
                </div>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => deleteGroup(g.id)}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="admin-info">
        <strong>How it works</strong>
        <ul>
          <li>Create groups (SIT, CPC, etc.)</li>
          <li>Assign groups to users</li>
          <li>Assign groups to folders/subfolders</li>
        </ul>
      </div>
    </div>
  );

  function loadGroups() {
    (async () => {
      const snap = await getDocs(collection(db, "groups"));
      setGroups(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })().catch((e) => {
      console.error("loadGroups failed:", e);
    });
  }
}
